import * as Sentry from "@sentry/nextjs";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";

import { useErrorToaster } from "src/common/components/ErrorDetails";
import { client, endpoint } from "src/lib/bridgeClient";
import { SERVER_COMM_ERROR } from "src/lib/error";

export type UpdateTemplateDTO = {
  workflow_name: string;
  template_name: string;
  is_gpu: boolean;
  head_node_instance_type: string;
  compute_node_instance_type: string;
  volume_id?: string;
  tags: string;
  scheduler_id: number;
  blueprint_id: number;
  id: number;
};

const updateTemplate = async (client: Client, template: UpdateTemplateDTO) => {
  return await client(endpoint.updateWorkflowTemplate(template.id), "PATCH", template);
};

const updateOrgTemplate = async (client: Client, template: UpdateTemplateDTO) => {
  return await client(endpoint.updateOrgWorkflowTemplate(template.id), "PATCH", template);
};

export const useUpdateTemplate = () => {
  const { mutate } = useSWRConfig();
  const toastError = useErrorToaster();

  return async (template: UpdateTemplateDTO): Promise<AxiosResponse> => {
    let response: AxiosResponse;

    try {
      response = await updateTemplate(client, template);

      mutate(endpoint.myWorkflowTemplates);

      toast.success(`Workflow template updated: ${template.template_name}`);
    } catch (error) {
      if (error?.response?.data?.error) {
        toastError("Failed to update workflow template", error?.response?.data?.error);
      } else {
        toast.error(`Request failed: ${SERVER_COMM_ERROR}`);
        Sentry.captureException(error);
      }

      return error.response;
    }

    return response;
  };
};

export const useUpdateOrgTemplate = () => {
  const { mutate } = useSWRConfig();
  const toastError = useErrorToaster();

  return async (template: UpdateTemplateDTO): Promise<AxiosResponse> => {
    let response: AxiosResponse;

    try {
      response = await updateOrgTemplate(client, template);

      mutate(endpoint.myOrgWorkflowTemplates);

      toast.success(`Workflow template updated: ${template.template_name}`);
    } catch (error) {
      if (error?.response?.data?.error) {
        toastError("Failed to update workflow template", error?.response?.data?.error);
      } else {
        toast.error(`Request failed: ${SERVER_COMM_ERROR}`);
        Sentry.captureException(error);
      }

      return error.response;
    }

    return response;
  };
};
